import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { Subject, takeUntil } from 'rxjs';
import { PromptCard } from 'src/app/routes/ai/models/prompt.model';
import { PromptCardPollingService } from 'src/app/routes/ai/services/prompt-card-polling.service';
import { PromptLibraryService } from 'src/app/routes/ai/services/prompt-library.service';

interface PromptRunItem {
  executionId: number;
  promptCardId: number;
  title: string;
  status: string;
  progress: number;
  updatedAt: number;
  startedAt: number;
  analyzingAt?: number;
}

@Component({
  selector: 'app-prompt-card-runner',
  standalone: true,
  imports: [CommonModule, NzButtonModule, NzIconModule, NzProgressModule],
  templateUrl: './prompt-card-runner.component.html',
  styleUrls: ['./prompt-card-runner.component.less']
})
export class PromptCardRunnerComponent implements OnInit, OnDestroy {
  runs: PromptRunItem[] = [];
  private readonly destroyed$ = new Subject<void>();
  private readonly titleMap = new Map<number, string>();
  private elapsedTimeTimer: ReturnType<typeof setInterval> | null = null;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly promptLibraryService: PromptLibraryService,
    private readonly promptCardPolling: PromptCardPollingService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.refreshPrompts();
    this.promptCardPolling.updates$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(update => this.applyPollingUpdate(update.promptId, update.data));
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.clearElapsedTimeTimer();
    this.stopRefreshTimer();
  }

  /**
   * Returns status-specific display text.
   */
  getStatusText(run: PromptRunItem): string {
    switch (run.status) {
      case 'Pending':
        return 'Queued...';
      case 'Running':
        return 'Analyzing...';
      case 'Failed':
        return 'Failed';
      case 'Completed':
        return 'Completed';
      default:
        return run.status;
    }
  }

  /**
   * Calculates and returns the elapsed time since analyzing started.
   * Only counts from when the status transitions to 'Running' (Analyzing).
   */
  getElapsedTime(run: PromptRunItem): string | null {
    if (run.status !== 'Running') return null;
    if (!run.analyzingAt) return null;

    const diffMs = Date.now() - run.analyzingAt;
    if (diffMs < 0) return null;

    const totalSeconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }

  /**
   * Returns the appropriate icon for the run status.
   */
  getStatusIcon(run: PromptRunItem): string {
    switch (run.status) {
      case 'Pending':
        return 'hourglass';
      case 'Running':
        return 'loading';
      case 'Failed':
        return 'close-circle';
      case 'Completed':
        return 'check-circle';
      default:
        return 'info-circle';
    }
  }

  private startElapsedTimeTimer(): void {
    if (this.elapsedTimeTimer) return;
    this.elapsedTimeTimer = setInterval(() => {
      if (this.hasActiveRuns()) {
        this.cdr.markForCheck();
      } else {
        this.clearElapsedTimeTimer();
      }
    }, 1000);
  }

  private clearElapsedTimeTimer(): void {
    if (this.elapsedTimeTimer) {
      clearInterval(this.elapsedTimeTimer);
      this.elapsedTimeTimer = null;
    }
  }

  closeRun(run: PromptRunItem): void {
    this.runs = this.runs.filter(item => item.executionId !== run.executionId);
    this.cdr.markForCheck();
  }

  openPrompt(run: PromptRunItem): void {
    // Add timestamp to force Angular Router to trigger a new navigation even if params are the same
    this.router.navigate(['/ai', 'copilot'], {
      queryParams: {
        openPrompt: 'true',
        promptId: run.promptCardId,
        executionId: run.executionId,
        _t: Date.now() // Force fresh navigation
      }
    });
  }

  trackByExecutionId(_: number, item: PromptRunItem): number {
    return item.executionId;
  }

  isIndeterminate(run: PromptRunItem): boolean {
    return run.status === 'Pending' || run.status === 'Running';
  }

  hasActiveRuns(): boolean {
    return this.runs.some(run => this.isIndeterminate(run));
  }

  private syncFromPrompts(prompts: PromptCard[]): void {
    const promptMap = new Map<number, PromptCard>();
    prompts.forEach(prompt => {
      promptMap.set(prompt.id, prompt);
      if (prompt.title) {
        this.titleMap.set(prompt.id, prompt.title);
      }
    });

    this.runs.forEach(run => {
      const prompt = promptMap.get(run.promptCardId);
      if (!prompt) return;

      const status = prompt.lastExecutionStatus || run.status;
      const executionId = prompt.lastExecutionId || run.executionId;

      if (executionId && executionId !== run.executionId) {
        run.executionId = executionId;
        run.startedAt = prompt.lastExecutionStartedAt
          ? new Date(prompt.lastExecutionStartedAt).getTime()
          : Date.now();
        // Reset analyzingAt for new execution
        run.analyzingAt = undefined;
      }

      const prevStatus = run.status;
      run.status = status;
      run.progress =
        status === 'Completed' || status === 'Failed'
          ? 100
          : prompt.lastExecutionProgress ?? 0;
      run.updatedAt = Date.now();
      if (run.title === 'Loading...' && this.titleMap.has(run.promptCardId)) {
        run.title = this.titleMap.get(run.promptCardId)!;
      }
      // Set analyzingAt when first transitioning to Running
      if (status === 'Running' && prevStatus !== 'Running' && !run.analyzingAt) {
        run.analyzingAt = prompt.lastExecutionAnalyzingAt
          ? new Date(prompt.lastExecutionAnalyzingAt).getTime()
          : Date.now();
      }
    });

    prompts.forEach(prompt => {
      const status = prompt.lastExecutionStatus;
      if (!prompt.lastExecutionId) return;
      if (status !== 'Pending' && status !== 'Running') return;

      const existing = this.runs.find(
        item =>
          item.executionId === prompt.lastExecutionId ||
          item.promptCardId === prompt.id
      );
      if (existing) return;

      this.runs.unshift({
        executionId: prompt.lastExecutionId,
        promptCardId: prompt.id,
        title: prompt.title || 'Loading...',
        status: status || 'Running',
        progress: prompt.lastExecutionProgress ?? 0,
        updatedAt: Date.now(),
        startedAt: prompt.lastExecutionStartedAt
          ? new Date(prompt.lastExecutionStartedAt).getTime()
          : Date.now(),
        analyzingAt:
          status === 'Running' && prompt.lastExecutionAnalyzingAt
            ? new Date(prompt.lastExecutionAnalyzingAt).getTime()
            : status === 'Running'
              ? Date.now()
              : undefined
      });
    });

    this.trimRuns();
    if (this.hasActiveRuns()) {
      this.startElapsedTimeTimer();
      this.startRefreshTimer();
    } else {
      this.clearElapsedTimeTimer();
      this.stopRefreshTimer();
    }
    this.cdr.markForCheck();
  }

  private applyPollingUpdate(promptId: number, data: any): void {
    if (!promptId) return;

    const status: string | undefined = data?.status;
    if (!status) return;

    const executionId = Number(data?.executionId || data?.executionID);
    const title = this.titleMap.get(promptId) || 'Loading...';

    let existing = Number.isFinite(executionId)
      ? this.runs.find(item => item.executionId === executionId)
      : undefined;
    if (!existing) {
      existing = this.runs.find(item => item.promptCardId === promptId);
    }

    if (existing) {
      existing.executionId = Number.isFinite(executionId)
        ? executionId
        : existing.executionId;
      const prevStatus = existing.status;
      existing.status = status;
      existing.progress = status === 'Completed' || status === 'Failed' ? 100 : 0;
      existing.updatedAt = Date.now();
      if (existing.title === 'Loading...') {
        existing.title = title;
      }
      if (data?.startedAt) {
        existing.startedAt = new Date(data.startedAt).getTime();
      }
      // Set analyzingAt when first transitioning to Running
      if (status === 'Running' && prevStatus !== 'Running' && !existing.analyzingAt) {
        existing.analyzingAt = Date.now();
      }
    } else {
      this.runs.unshift({
        executionId: Number.isFinite(executionId) ? executionId : Date.now(),
        promptCardId: promptId,
        title,
        status,
        progress: status === 'Completed' || status === 'Failed' ? 100 : 0,
        updatedAt: Date.now(),
        startedAt: data?.startedAt ? new Date(data.startedAt).getTime() : Date.now(),
        analyzingAt: status === 'Running' ? Date.now() : undefined
      });
    }

    this.trimRuns();
    if (this.hasActiveRuns()) {
      this.startElapsedTimeTimer();
      this.startRefreshTimer();
    } else {
      this.clearElapsedTimeTimer();
      this.stopRefreshTimer();
    }
    this.cdr.markForCheck();
  }

  private refreshPrompts(): void {
    this.promptLibraryService
      .getPrompts()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(prompts => this.syncFromPrompts(prompts));
  }

  private startRefreshTimer(): void {
    if (this.refreshTimer) return;
    this.refreshTimer = setInterval(() => {
      this.refreshPrompts();
    }, 10000);
  }

  private stopRefreshTimer(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private trimRuns(): void {
    if (this.runs.length <= 5) return;
    this.runs = this.runs.slice(0, 5);
  }
}
